// ABOUTME: Body of the delete confirmation: what applying the delete run removes, each object counted once.
// ABOUTME: Shows a progress line while the impact loads and the error when it cannot be counted.

import { Alert, CircularProgress, Stack, Typography } from '@ds/coolshop';
import { useIntl } from 'react-intl';

import { DeleteImpactDto, DeleteNodeAction } from '../types';

type DeleteImpactMessageProps = {
    impact: DeleteImpactDto | null;
    loading: boolean;
    error: Error | null;
    linkedAction: DeleteNodeAction['linkedAction'];
};

export function DeleteImpactMessage({
    impact,
    loading,
    error,
    linkedAction,
}: DeleteImpactMessageProps) {
    const { formatMessage } = useIntl();

    if (error) {
        return (
            <Alert severity="error">
                {formatMessage(
                    {
                        id: 'bulkChange.confirmDelete.error',
                        defaultMessage: 'Could not count what will be deleted: {error}',
                    },
                    { error: error.message },
                )}
            </Alert>
        );
    }

    if (loading || !impact) {
        return (
            <Stack direction="row" gap={1} alignItems="center">
                <CircularProgress size="small" />
                <Typography variant="body1">
                    {formatMessage({
                        id: 'bulkChange.confirmDelete.loading',
                        defaultMessage: 'Counting what will be deleted…',
                    })}
                </Typography>
            </Stack>
        );
    }

    return (
        <Stack gap={1}>
            <Typography variant="body1">
                {formatMessage(
                    {
                        id: 'bulkChange.confirmDelete.message',
                        defaultMessage:
                            '{objects, plural, one {# object} other {# objects}} will be deleted{descendants, plural, =0 {} one {, together with # descendant} other {, together with # descendants}}. This cannot be undone.',
                    },
                    { objects: impact.objects, descendants: impact.descendants },
                )}
            </Typography>
            {linkedAction === 'cascade' && impact.linked > 0 && (
                <Typography variant="body1">
                    {formatMessage(
                        {
                            id: 'bulkChange.confirmDelete.linked',
                            defaultMessage:
                                '{linked, plural, one {# object linking to them is deleted too} other {# objects linking to them are deleted too}}, in other hierarchies as well.',
                        },
                        { linked: impact.linked },
                    )}
                </Typography>
            )}
        </Stack>
    );
}
